import { useEffect, useState } from "react";

interface SplashScreenProps {
  onFinish: () => void;
}

export default function SplashScreen({ onFinish }: SplashScreenProps) {
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHiding(true);
      setTimeout(onFinish, 700);
    }, 3000);
    return () => clearTimeout(timer);
  }, [onFinish]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#0d1b26] transition-opacity duration-700 ${hiding ? "opacity-0 pointer-events-none" : "opacity-100"}`}
    >
      {/* Fondo gradiente radial */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 55%, #f97316 0%, transparent 60%)",
        }}
      />

      {/* Contenedor principal de la grúa + logo colgante */}
      <div
        className="relative"
        style={{ width: 340, height: 380 }}
      >
        {/* ── TORRE VERTICAL ── */}
        <div
          className="absolute bg-orange-500 rounded-sm"
          style={{ width: 14, height: 160, bottom: 18, left: "50%", transform: "translateX(-50%)" }}
        />

        {/* ── CABINA ── */}
        <div
          className="absolute bg-orange-600 rounded-t-md"
          style={{ width: 30, height: 20, bottom: 176, left: "50%", transform: "translateX(-50%)" }}
        />

        {/* ── BRAZO OSCILANTE (contiene cable + logo) ── */}
        <div
          className="absolute"
          style={{
            bottom: 168,
            left: "50%",
            transformOrigin: "0% 50%",
            animation: "crane-arm 2.5s ease-in-out infinite alternate",
          }}
        >
          {/* Brazo principal hacia la derecha */}
          <div
            className="bg-orange-400 rounded-sm"
            style={{ width: 150, height: 11, marginTop: -5.5 }}
          />
          {/* Brazo contrapeso hacia la izquierda */}
          <div
            className="absolute bg-orange-600 rounded-sm"
            style={{ width: 55, height: 9, top: 1, right: "100%", transform: "translateX(0)" }}
          />
          {/* Contrapeso */}
          <div
            className="absolute bg-orange-700 rounded-sm"
            style={{ width: 22, height: 16, top: -3, left: -77 }}
          />

          {/* ── CABLE ── */}
          <div
            style={{
              position: "absolute",
              top: 11,
              right: 24,
              width: 2,
              height: 70,
              background: "linear-gradient(to bottom, #d1d5db, #9ca3af)",
              transformOrigin: "top center",
              animation: "cable-swing 2.5s ease-in-out infinite alternate",
            }}
          />

          {/* ── GANCHO ── */}
          <div
            style={{
              position: "absolute",
              top: 79,
              right: 19,
              width: 12,
              height: 10,
              backgroundColor: "#9ca3af",
              borderRadius: "0 0 6px 6px",
              transformOrigin: "top center",
              animation: "cable-swing 2.5s ease-in-out infinite alternate",
            }}
          />

          {/* ── LOGO COLGANTE ── */}
          <div
            style={{
              position: "absolute",
              top: 89,
              right: -42,
              width: 110,
              transformOrigin: "top center",
              animation: "cable-swing 2.5s ease-in-out infinite alternate",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Cadenas decorativas */}
            <div style={{ display: "flex", gap: 28, marginBottom: 2 }}>
              <div style={{ width: 2, height: 10, background: "#6b7280" }} />
              <div style={{ width: 2, height: 10, background: "#6b7280" }} />
            </div>
            {/* Caja del logo */}
            <div
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1.5px solid rgba(249,115,22,0.4)",
                borderRadius: 10,
                padding: "10px 14px",
                backdropFilter: "blur(4px)",
              }}
            >
              <img
                src="https://public.readdy.ai/ai/img_res/8c1892ea-8cdb-400c-8091-5e15e8393279.png"
                alt="Charles Juárez"
                style={{
                  height: 52,
                  width: "auto",
                  objectFit: "contain",
                  filter: "brightness(0) invert(1)",
                  display: "block",
                }}
              />
            </div>
          </div>
        </div>

        {/* ── RUEDAS ── */}
        {[-34, -14, 6, 26].map((offset) => (
          <div
            key={offset}
            style={{
              position: "absolute",
              bottom: 0,
              left: `calc(50% + ${offset}px)`,
              transform: "translateX(-50%)",
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: "#374151",
              border: "2px solid #4b5563",
              animation: "wheel-spin 1s linear infinite",
            }}
          />
        ))}

        {/* Suelo */}
        <div
          className="absolute bottom-0 left-0 right-0 rounded-full"
          style={{ height: 2, background: "rgba(249,115,22,0.2)" }}
        />
      </div>

      {/* Tagline */}
      <p className="relative z-10 text-white/35 text-xs tracking-[0.3em] uppercase mt-2 mb-8">
        Maquinarias &amp; Maestranza
      </p>

      {/* Barra de progreso */}
      <div className="relative z-10 w-48 h-0.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-orange-500 rounded-full"
          style={{ animation: "progress-bar 2.8s ease-in-out forwards" }}
        />
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes crane-arm {
          0%   { transform: rotate(-5deg); }
          100% { transform: rotate(5deg); }
        }
        @keyframes cable-swing {
          0%   { transform: rotate(-7deg); }
          100% { transform: rotate(7deg); }
        }
        @keyframes wheel-spin {
          from { transform: translateX(-50%) rotate(0deg); }
          to   { transform: translateX(-50%) rotate(360deg); }
        }
        @keyframes progress-bar {
          0%   { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
}
